// Copyright 2021 Amazon.com, Inc. or its affiliates. All Rights Reserved.

import KatalLogger, { LoggerConfig } from "@amzn/katal-logger";
import { METRIC_NAME, TOOLBAR_METRIC_NAME } from "./metricName";
import {
    createEmfFormattedMetric,
    createEmfHttpStatusCodeMetric,
    DimensionSet,
    EmfHttpStatusCodeMetric,
    EmfMetric,
} from "./emfUtils";

type ErmineConfig = LoggerConfig;

/**
 * HTTP status code categories mapped to their metric outcomes
 */
export enum MetricOutcome {
    SUCCESS = "Success",
    ERROR = "Error",
    FAULT = "Fault",
}

export class ErmineLogger extends KatalLogger {
    constructor(config?: ErmineConfig) {
        super({ ...config });
    }

    public logMessage(
        message: string,
        context?: Record<string, unknown>
    ): void {
        context = context || {};
        context.userSub = window.userSub;
        this.info(message, context);
    }

    public publishCounterMetric(
        metricName: METRIC_NAME | TOOLBAR_METRIC_NAME,
        count = 1
    ): void {
        const emfMetric: EmfMetric = {
            name: metricName,
            value: count,
            unit: "Count",
            dimensions: {},
        };
        this.info(metricName, createEmfFormattedMetric(emfMetric));
    }

    public publishMetricWithDimension(
        metricName: METRIC_NAME | TOOLBAR_METRIC_NAME,
        dimensionValue: string
    ): void {
        const emfMetric: EmfMetric = {
            name: metricName,
            value: 1,
            unit: "Count",
            dimensions: { dimension: dimensionValue },
        };
        this.info(metricName, createEmfFormattedMetric(emfMetric));
    }

    public publishMetricWithDimensions(
        metricName: METRIC_NAME | TOOLBAR_METRIC_NAME,
        dimensions: DimensionSet,
        value = 1
    ): void {
        const emfMetric: EmfMetric = {
            name: metricName,
            value: value,
            unit: "Count",
            dimensions,
        };
        this.info(metricName, createEmfFormattedMetric(emfMetric));
    }

    public publishNumericMetric(
        metricName: METRIC_NAME | TOOLBAR_METRIC_NAME,
        value: number,
        dimensions?: DimensionSet
    ): void {
        const emfMetric: EmfMetric = {
            name: metricName,
            value: value,
            unit: "None",
            dimensions: dimensions ?? {},
        };
        this.info(metricName, createEmfFormattedMetric(emfMetric));
    }

    /**
     * Publishes metrics following Coral's naming convention where:
     * 2xx -> Success
     * 4xx -> Error
     * 5xx -> Fault
     *
     * @param metricName - The operation/API name
     * @param httpStatus - HTTP status code
     */
    public publishHttpStatusMetric(
        metricName: METRIC_NAME,
        httpStatus: number
    ): void {
        let outcome: MetricOutcome;

        if (httpStatus >= 200 && httpStatus < 300) {
            outcome = MetricOutcome.SUCCESS;
        } else if (httpStatus >= 400 && httpStatus < 500) {
            outcome = MetricOutcome.ERROR;
        } else if (httpStatus >= 500 && httpStatus < 600) {
            outcome = MetricOutcome.FAULT;
        } else {
            console.warn(
                `An unexpected httpStatusCode value was returned from ${metricName}`
            );
            return;
        }

        const emfMetric: EmfHttpStatusCodeMetric = {
            name: outcome,
            value: 1,
            unit: "Count",
            httpStatusCode: httpStatus,
            dimensions: {
                Operation: metricName,
            },
        };
        this.info(metricName, createEmfHttpStatusCodeMetric(emfMetric));
    }
}
